import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Bell, MessageSquare, ScanLine, Printer, Search, ShieldOff, RefreshCw, ArrowUp, Download, MailPlus, Pencil, Ticket, AlertTriangle, Clock, X, Trash2, StickyNote, Undo2 } from "lucide-react";
import api, { errText } from "@/lib/api";
import { Shell } from "@/components/Shell";
import { Badge } from "@/components/Badge";
import { ROLES, roleOf } from "@/lib/roles";
import { MessagesTab } from "@/components/MessagesTab";
import { EventForm } from "@/components/EventForm";
import { AttendeeEdit } from "@/components/AttendeeEdit";import { AttendeeNote } from "@/components/AttendeeNote";
import { StationBoard } from "@/components/StationBoard";
import { useAuth } from "@/context/AuthContext";
import { SALUTATIONS } from "@/lib/salutations";

const BULK_LABELS = {
  invite: "Invitations",
  ticket: "Passes",
  reminder: "Reminders",
  survey: "Surveys",
  notice: "Confirmation",
};

const NOTICE_AUDIENCES = [
  ["all", "Everyone registered"],
  ["confirmed", "Confirmed only"],
  ["rsvp_yes", "RSVP yes only"],
];

// A custom message has no fixed label, so fall back to the name the organiser gave it.
const labelOf = (kind, customs) =>
  BULK_LABELS[kind] || customs.find((c) => c.key === kind)?.name || "message";

// Scheduled sends are stored and shown in Singapore time (GMT+8).
const fmtWhen = (local) => {
  const [d, t] = (local || "").split("T");
  if (!d) return "";
  const date = new Date(`${d}T00:00:00`);
  return `${date.getDate()} ${date.toLocaleString("en-GB", { month: "short" })}, ${t}`;
};

const defaultWhen = () => {
  // Prefill tomorrow 09:00 in Singapore time, whatever the organiser's own clock says.
  const sgt = new Date(Date.now() + 24 * 60 * 60 * 1000 + 8 * 60 * 60 * 1000);
  return `${sgt.toISOString().slice(0, 10)}T09:00`;
};

const Stat = ({ label, value, accent }) => (
  <div className="card p-5">
    <p className="label">{label}</p>
    <p className={`font-display text-4xl font-black ${accent || "text-slate-100"}`}>{value}</p>
  </div>
);

export default function EventCommand() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [stats, setStats] = useState(null);
  const [customs, setCustoms] = useState([]);
  const [customAudience, setCustomAudience] = useState({});
  const [regs, setRegs] = useState([]);
  const [awardRecipients, setAwardRecipients] = useState([]);
  const [q, setQ] = useState("");
  const [regFilter, setRegFilter] = useState("all");
  const [regSort, setRegSort] = useState("registered_desc");
  const [tableFilter, setTableFilter] = useState("");
  const [groupTables, setGroupTables] = useState(false);
  const [tables, setTables] = useState({ tables: [], unassigned: 0 });
  const [survey, setSurvey] = useState(null);
  const [tab, setTab] = useState("live");
  const [badge, setBadge] = useState(null);
  const [wa, setWa] = useState(null);
  const [waPhone, setWaPhone] = useState("");
  const [delivery, setDelivery] = useState(null);
  const [dietary, setDietary] = useState(null);
  const [editing, setEditing] = useState(false);
  const [sending, setSending] = useState("");
  const [confirm, setConfirm] = useState(null);
  const [schedules, setSchedules] = useState([]);
  const [editAttendee, setEditAttendee] = useState(null);
  const [noticeAudience, setNoticeAudience] = useState("all");
  const [deleteAttendee, setDeleteAttendee] = useState(null);
  const [noteAttendee, setNoteAttendee] = useState(null);
  const [scheduling, setScheduling] = useState(null);
  const [lastSend, setLastSend] = useState(null);

  const loadSchedules = useCallback(async () => {
    try {
      const { data } = await api.get(`/events/${eventId}/schedules`);
      setSchedules(data.schedules);
    } catch (e) {
      /* schedules are non-critical for the live view */
    }
  }, [eventId]);

  const loadDietary = useCallback(async () => {
    try {
      const { data } = await api.get(`/events/${eventId}/report/dietary`);
      setDietary(data);
    } catch (e) {
      toast.error(errText(e));
    }
  }, [eventId]);

  const loadDelivery = useCallback(async () => {
    try {
      const { data } = await api.get(`/events/${eventId}/delivery`);
      setDelivery(data);
    } catch (e) {
      toast.error(errText(e));
    }
  }, [eventId]);

  const download = async (kind) => {
    try {
      const { data, headers } = await api.get(`/events/${eventId}/export/${kind}.csv`, { responseType: "blob" });
      const match = /filename="([^"]+)"/.exec(headers?.["content-disposition"] || "");
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = match ? match[1] : `${kind}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`${kind}.csv downloaded`);
    } catch (e) {
      toast.error(errText(e));
    }
  };

  const downloadRsvpExcel = async () => {
    try {
      const { data, headers } = await api.get(`/events/${eventId}/export/rsvp.xlsx`, {
        responseType: "blob",
      });
      const match = /filename="([^"]+)"/.exec(headers?.["content-disposition"] || "");
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = match ? match[1] : "rsvp.xlsx";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("RSVP workbook downloaded");
    } catch (e) {
      toast.error(errText(e));
    }
  };

  const downloadReport = async (format) => {
    try {
      const { data, headers } = await api.get(`/events/${eventId}/report/daily.${format}`, {
        responseType: "blob",
      });
      const match = /filename="([^"]+)"/.exec(headers?.["content-disposition"] || "");
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = match ? match[1] : `rsvp-report.${format}`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`RSVP report downloaded as ${format.toUpperCase()}`);
    } catch (e) {
      toast.error(errText(e));
    }
  };

  const downloadAttendancePdf = async () => {
    try {
      const { data, headers } = await api.get(`/events/${eventId}/report/attendance.pdf`, {
        responseType: "blob",
      });
      const match = /filename="([^"]+)"/.exec(headers?.["content-disposition"] || "");
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = match ? match[1] : "attendance-report.pdf";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Attendance report downloaded");
    } catch (e) {
      toast.error(errText(e));
    }
  };

  const downloadSeatingPdf = async () => {
    try {
      const { data, headers } = await api.get(`/events/${eventId}/report/seating.pdf`, {
        responseType: "blob",
      });
      const match = /filename="([^"]+)"/.exec(headers?.["content-disposition"] || "");
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = match ? match[1] : "seating-sheet.pdf";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Seating sheet downloaded");
    } catch (e) {
      toast.error(errText(e));
    }
  };

  const downloadDietaryPdf = async () => {
    try {
      const { data, headers } = await api.get(`/events/${eventId}/report/dietary.pdf`, {
        responseType: "blob",
      });
      const match = /filename="([^"]+)"/.exec(headers?.["content-disposition"] || "");
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = match ? match[1] : "dietary-report.pdf";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Dietary report downloaded");
    } catch (e) {
      toast.error(errText(e));
    }
  };

  const testWhatsApp = async () => {
    try {
      const { data } = await api.post("/integrations/whatsapp/test", { phone: waPhone });
      toast.success(`Test message sent (${data.message_sid})`);
    } catch (e) {
      toast.error(errText(e));
    }
  };

  const loadStats = useCallback(async () => {
    try {
      const { data } = await api.get(`/events/${eventId}/stats`);
      setStats(data);
      setCustoms(data.event?.custom_messages || []);
      const all = await api.get(`/events/${eventId}/registrations`);
      setAwardRecipients(all.data.filter((r) => roleOf(r)));
    } catch (e) {
      toast.error(errText(e));
    }
  }, [eventId]);

  const regRequestRef = useRef(0);
  const loadRegs = useCallback(
    async (search = "", filter = "all", sort = "registered_desc", table = "") => {
      const ticket = ++regRequestRef.current;
      const { data } = await api.get(`/events/${eventId}/registrations`, {
        params: { q: search, filter, sort, table },
      });
      // A slow reply for an earlier keystroke must not overwrite the latest search.
      if (ticket === regRequestRef.current) setRegs(data);
    },
    [eventId]
  );

  const attendeeRows = useMemo(() => {
    if (!groupTables) return regs;
    const groups = [];
    regs.forEach((r) => {
      const label = (r.table || "").trim() || "No table yet";
      const last = groups[groups.length - 1];
      if (last && last.label === label) last.rows.push(r);
      else groups.push({ label, rows: [r] });
    });
    return groups.flatMap((g) => [
      { __group: true, label: g.label, count: g.rows.length },
      ...g.rows,
    ]);
  }, [regs, groupTables]);

  const loadTables = useCallback(async () => {    const { data } = await api.get(`/events/${eventId}/tables`);
    setTables(data);
  }, [eventId]);

  useEffect(() => {
    const t = setTimeout(
      () => loadRegs(q, regFilter, regSort, tableFilter).catch((e) => toast.error(errText(e))),
      300
    );
    return () => clearTimeout(t);
  }, [q, regFilter, regSort, tableFilter, loadRegs]);

  useEffect(() => {
    if (!loading && !user) navigate("/moderator/login");
  }, [user, loading, navigate]);

  useEffect(() => {
    loadStats();
    loadRegs();
    loadTables().catch(() => {});
    api.get(`/events/${eventId}/survey-results`).then(({ data }) => setSurvey(data)).catch(() => {});
    api.get("/integrations/whatsapp").then(({ data }) => setWa(data)).catch(() => {});
    loadSchedules();
    const t = setInterval(loadStats, 5000);
    return () => clearInterval(t);
  }, [eventId, loadStats, loadRegs, loadSchedules]);
  useEffect(() => {
    if (!badge) return;
    const onKey = (e) => e.key === "Escape" && setBadge(null);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [badge]);

  const askSend = async (kind, onlyFailed = false, audience = "all") => {
    try {
      const { data } = await api.get(`/events/${eventId}/notify/${kind}/recipients`, {
        params: { only_failed: onlyFailed, audience },
      });
      if (data.count === 0) {
        toast.info(`No one to send ${labelOf(kind, customs).toLowerCase()} to right now`);
        return;
      }
      setConfirm({ ...data, onlyFailed, audience });
    } catch (e) {
      toast.error(errText(e));
    }
  };

  const runSend = async () => {
    const { kind, onlyFailed, audience } = confirm;
    setConfirm(null);
    setSending(`bulk:${kind}`);
    try {
      const { data } = await api.post(`/events/${eventId}/notify/${kind}`, null, {
        params: { only_failed: onlyFailed, audience: audience || "all" },
      });
      setLastSend(data);
      const label = labelOf(kind, customs);
      if (data.rate_limited)
        toast.warning(
          `${label} paused: ${data.sent} sent, then the email provider's limit was reached. ` +
            `${data.not_attempted} still to go — retry later.`
        );
      else if (data.failed)
        toast.warning(`${label}: ${data.sent} sent, ${data.failed} failed. Use "Retry failed".`);
      else toast.success(`${label} sent to ${data.sent} attendee(s)`);
      if (!data.whatsapp_enabled) toast.info("WhatsApp is not configured — email only.");
      loadStats();
      loadDelivery();
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setSending("");
    }
  };

  const setDailyReport = async (enabled) => {
    try {
      const { data } = await api.put(`/events/${eventId}/daily-report`, { enabled });
      setStats((s) => ({ ...s, daily_report: data.config }));
      toast.success(enabled ? "Daily report is on" : "Daily report is off");
    } catch (e) {
      toast.error(errText(e));
    }
  };

  const sendDailyReportNow = async () => {
    setSending("daily-report");
    try {
      const { data } = await api.post(`/events/${eventId}/daily-report/send`);
      toast.success(`Report sent to ${data.sent_to} — ${data.report.rsvp_yes} replied yes`);
      loadStats();
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setSending("");
    }
  };

  const saveSchedule = async () => {    const { kind, onlyFailed, when } = scheduling;
    if (!when) {
      toast.error("Pick a date and time first");
      return;
    }
    try {
      if (scheduling.replace) {
        await api.delete(`/events/${eventId}/schedules/${scheduling.replace.schedule_id}`);
      }
      const { data } = await api.post(`/events/${eventId}/notify/${kind}/schedule`, {
        run_at_local: when,
        only_failed: !!onlyFailed,
        audience: scheduling.audience || "all",
      });
      setScheduling(null);
      toast.success(`${labelOf(kind, customs)} scheduled for ${fmtWhen(data.run_at_local)} SGT`);
      loadSchedules();
    } catch (e) {
      toast.error(errText(e));
    }
  };

  const cancelSchedule = async (s) => {
    try {
      await api.delete(`/events/${eventId}/schedules/${s.schedule_id}`);
      toast.success("Scheduled send cancelled");
      loadSchedules();
    } catch (e) {
      toast.error(errText(e));
    }
  };

  const saveSettings = async (patch) => {
    const next = {
      passes_released: stats.passes_released,
      rsvp_required: stats.rsvp_required,
      auto_email_on_register: stats.auto_email_on_register,
      auto_pass_on_rsvp: stats.auto_pass_on_rsvp,
      ...patch,
    };
    setStats({ ...stats, ...next });
    try {
      await api.post(`/events/${eventId}/settings`, next);
      toast.success("Event settings updated");
    } catch (e) {
      toast.error(errText(e));
    } finally {
      loadStats();
    }
  };

  const manualCheckIn = async (r) => {
    try {
      const { data } = await api.post(`/checkins/manual/${r.registration_id}`, null, {
        params: { station_id: "manual-desk" },
      });
      if (data.result === "success") {
        toast.success(`${r.first_name} checked in`);
        setBadge({ registration: data.registration, event: stats.event });
      } else toast.warning(data.message);
      loadStats();
      loadRegs(q, regFilter, regSort, tableFilter);
    } catch (e) {
      toast.error(errText(e));
    }
  };

  const undoCheckIn = async (r) => {
    if (!window.confirm(`Undo the check-in for ${r.first_name} ${r.last_name}? Your attendance figures will change.`))
      return;
    try {
      const { data } = await api.post(
        `/events/${eventId}/registrations/${r.registration_id}/undo-checkin`
      );
      toast.success(data.message);
      loadRegs(q, regFilter, regSort, tableFilter);
      loadStats();
    } catch (e) {
      toast.error(errText(e));
    }
  };

  const saveRole = async (r, role) => {
    try {
      await api.post(`/events/${eventId}/registrations/${r.registration_id}/award`, { role });
      setRegs((prev) =>
        prev.map((x) =>
          x.registration_id === r.registration_id
            ? { ...x, guest_role: role, award_recipient: role === "award" }
            : x
        )
      );
      toast.success(role ? `${r.first_name} tagged as ${ROLES[role].label}` : "Role removed");
      loadStats();
    } catch (e) {
      toast.error(errText(e));
    }
  };

  const saveTable = async (r, value) => {
    const table = value.trim();
    if (table === (r.table || "")) return;
    try {
      await api.post(`/events/${eventId}/registrations/${r.registration_id}/table`, { table });
      setRegs((prev) =>
        prev.map((x) => (x.registration_id === r.registration_id ? { ...x, table } : x))
      );
      loadTables().catch(() => {});
      toast.success(table ? `${r.first_name} seated at ${table}` : "Table cleared");
    } catch (e) {
      toast.error(errText(e));
    }
  };

  const setSalutation = async (r, value) => {
    let salutation = value;
    if (value === "__other") {
      salutation = (window.prompt("Salutation for this attendee", r.salutation || "") || "").trim();
      if (!salutation) return;
    }
    try {
      await api.post(`/events/${eventId}/registrations/${r.registration_id}/salutation`, { salutation });
      setRegs((prev) =>
        prev.map((x) => (x.registration_id === r.registration_id ? { ...x, salutation } : x))
      );
      toast.success(salutation ? `Salutation set to ${salutation}` : "Salutation cleared");
    } catch (e) {
      toast.error(errText(e));
    }
  };

  const removeAttendee = async () => {
    const r = deleteAttendee;
    setDeleteAttendee(null);
    try {
      await api.delete(`/events/${eventId}/registrations/${r.registration_id}`);
      toast.success(`${r.first_name} ${r.last_name} deleted`);
      loadStats();
      loadRegs(q, regFilter, regSort, tableFilter);
      loadDelivery();
    } catch (e) {
      toast.error(errText(e));
    }
  };

  const setStage = async (r, stage) => {
    try {
      await api.post(`/events/${eventId}/registrations/${r.registration_id}/stage`, { stage });
      const rsvpNote = stage === "confirmed" ? " · RSVP marked yes" : stage === "declined" ? " · RSVP marked no" : "";
      toast.success(stage === "auto" ? "Status back to automatic" : `Status set to ${stage}${rsvpNote}`);
      loadStats();
      loadRegs(q, regFilter, regSort, tableFilter);
    } catch (e) {
      toast.error(errText(e));
    }
  };

  const sendOne = async (r, kind) => {
    setSending(`${r.registration_id}:${kind}`);
    try {
      const { data } = await api.post(`/events/${eventId}/registrations/${r.registration_id}/send/${kind}`);
      toast.success(`${kind} email sent to ${data.to}`);
      loadDelivery();
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setSending("");
    }
  };

  const promote = async (r) => {
    try {
      await api.post(`/events/${eventId}/registrations/${r.registration_id}/promote`);
      toast.success(`${r.first_name} promoted from waitlist`);
      loadStats();
      loadRegs(q, regFilter, regSort, tableFilter);
    } catch (e) {
      toast.error(errText(e));
    }
  };

  const purge = async () => {
    if (!window.confirm("Anonymise all personal data for this event? This cannot be undone.")) return;
    try {
      const { data } = await api.post(`/events/${eventId}/purge-personal-data`);
      toast.success(`${data.anonymised} record(s) anonymised`);
      loadRegs(q, regFilter, regSort, tableFilter);
    } catch (e) {
      toast.error(errText(e));
    }
  };

  if (!stats) return <Shell><p className="text-sm text-slate-500">Loading command center…</p></Shell>;

  const tabs = [
    ["live", "Live check-in"],
    ["attendees", "Attendees"],
    ["messages", "Messages"],
    ["delivery", "Delivery log"],
    ["report", "Report & feedback"],
  ];

  return (
    <Shell wide>
      <div data-testid="event-command-page">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="overline">Live command center</p>
            <h1 className="font-display text-4xl sm:text-5xl font-black uppercase tracking-tight leading-none mt-2">
              {stats.event.name}
            </h1>
            <p className="font-mono2 text-xs text-slate-500 mt-2">
              {stats.event.date} · {stats.event.venue} · /e/{stats.event.slug}
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            <button onClick={() => setEditing(true)} className="btn-secondary" data-testid="edit-event-btn">
              <Pencil size={15} /> Edit details
            </button>
            <Link to={`/scanner/${eventId}`} className="btn-primary" data-testid="open-scanner-btn">
              <ScanLine size={15} /> Open scanner
            </Link>
            {[["invite", "Send invitations", MailPlus]].map(([kind, label, Icon]) => (
              <div key={kind} className="flex">
                <button
                  onClick={() => askSend(kind)}
                  className="btn-secondary rounded-r-none"
                  disabled={!!sending}
                  data-testid={`send-${kind}-btn`}
                >
                  <Icon size={15} /> {label}
                </button>
                <button
                  onClick={() => setScheduling({ kind, when: defaultWhen() })}
                  className="btn-secondary rounded-l-none border-l-0 px-2.5"
                  disabled={!!sending}
                  title={`Schedule ${BULK_LABELS[kind].toLowerCase()} for later`}
                  data-testid={`schedule-${kind}-btn`}
                >
                  <Clock size={15} />
                </button>
              </div>
            ))}
            <div className="flex">
              <select
                className="input py-2 px-2 text-xs rounded-r-none w-[150px]"
                value={noticeAudience}
                onChange={(e) => setNoticeAudience(e.target.value)}
                title="Who receives the confirmation"
                data-testid="notice-audience-select"
              >
                {NOTICE_AUDIENCES.map(([id, label]) => (
                  <option key={id} value={id}>
                    {label}
                  </option>
                ))}
              </select>
              <button
                onClick={() => askSend("notice", false, noticeAudience)}
                className="btn-secondary rounded-none border-l-0 whitespace-nowrap"
                disabled={!!sending}
                data-testid="send-notice-btn"
              >
                <AlertTriangle size={15} /> Send confirmation
              </button>
              <button
                onClick={() =>
                  setScheduling({ kind: "notice", audience: noticeAudience, when: defaultWhen() })
                }
                className="btn-secondary rounded-l-none border-l-0 px-2.5"
                disabled={!!sending}
                title="Schedule this confirmation for later"
                data-testid="schedule-notice-btn"
              >
                <Clock size={15} />
              </button>
            </div>
            {[
              ["ticket", "Send passes", Ticket],
              ["reminder", "Send reminders", Bell],
              ["survey", "Send survey", MessageSquare],
            ].map(([kind, label, Icon]) => (              <div key={kind} className="flex">
                <button
                  onClick={() => askSend(kind)}
                  className="btn-secondary rounded-r-none"
                  disabled={!!sending}
                  data-testid={`send-${kind}-btn`}
                >
                  <Icon size={15} /> {label}
                </button>
                <button
                  onClick={() => setScheduling({ kind, when: defaultWhen() })}
                  className="btn-secondary rounded-l-none border-l-0 px-2.5"
                  disabled={!!sending}
                  title={`Schedule ${labelOf(kind, customs).toLowerCase()} for later`}
                  data-testid={`schedule-${kind}-btn`}
                >
                  <Clock size={15} />
                </button>
              </div>
            ))}
            {customs.map((c) => (
              <div key={c.key} className="flex" data-testid={`custom-send-${c.key}`}>
                <select
                  className="input py-2 px-2 text-xs rounded-r-none w-[150px]"
                  value={customAudience[c.key] || "all"}
                  onChange={(e) => setCustomAudience({ ...customAudience, [c.key]: e.target.value })}
                  title={`Who receives "${c.name}"`}
                  data-testid={`custom-audience-${c.key}`}
                >
                  {NOTICE_AUDIENCES.map(([id, label]) => (
                    <option key={id} value={id}>
                      {label}
                    </option>
                  ))}
                </select>
                <button
                  onClick={() => askSend(c.key, false, customAudience[c.key] || "all")}
                  className="btn-secondary rounded-l-none border-l-0 whitespace-nowrap"
                  disabled={!!sending}
                  data-testid={`send-${c.key}-btn`}
                >
                  <MailPlus size={15} /> Send {c.name}
                </button>
              </div>
            ))}
            {lastSend && (lastSend.failed > 0 || lastSend.not_attempted > 0) && (
              <button onClick={() => askSend(lastSend.kind, true)} className="btn-secondary" disabled={!!sending}
                data-testid="retry-failed-btn">
                <RefreshCw size={15} /> Retry failed ({lastSend.failed + lastSend.not_attempted})
              </button>
            )}
            {sending.startsWith("bulk:") && (
              <span className="font-mono2 text-xs text-[#ffb800] self-center" data-testid="bulk-sending-status">
                Sending {labelOf(sending.split(":")[1], customs).toLowerCase()}… please keep this tab open
              </span>
            )}
          </div>
        </div>

        {schedules.filter((s) => s.status === "pending").length > 0 && (
          <div className="flex flex-wrap gap-2 mt-5" data-testid="scheduled-sends">
            {schedules
              .filter((s) => s.status === "pending")
              .map((s) => (
                <span
                  key={s.schedule_id}
                  className="inline-flex items-center gap-2 font-mono2 text-[11px] px-3 py-1.5 rounded-full border border-[#00e599]/40 text-[#00e599]"
                  data-testid={`schedule-chip-${s.kind}`}
                >
                  <Clock size={12} />
                  {BULK_LABELS[s.kind] || s.kind} scheduled for {fmtWhen(s.run_at_local)} SGT
                  <button
                    onClick={() => setScheduling({ kind: s.kind, when: s.run_at_local, replace: s })}
                    className="underline hover:text-slate-100"
                    data-testid={`schedule-edit-${s.kind}`}
                  >
                    reschedule
                  </button>
                  <button
                    onClick={() => cancelSchedule(s)}
                    className="hover:text-red-400"
                    title="Cancel this scheduled send"
                    data-testid={`schedule-cancel-${s.kind}`}
                  >
                    <X size={12} />
                  </button>
                </span>
              ))}
          </div>
        )}


        {schedules.filter((s) => s.status !== "pending").slice(-1).map((s) => (
          <p key={s.schedule_id} className="font-mono2 text-[11px] text-slate-500 mt-3"
            data-testid="schedule-history">
            Last scheduled run: {BULK_LABELS[s.kind] || s.kind} at {fmtWhen(s.run_at_local)} SGT — {s.status}
            {s.result?.sent !== undefined && ` (${s.result.sent} sent`}
            {s.result?.not_attempted ? `, ${s.result.not_attempted} left to retry)` : s.result?.sent !== undefined ? ")" : ""}
          </p>
        ))}

        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mt-8" data-testid="live-stats">
          <Stat label="Registered" value={stats.confirmed} />
          <Stat label="RSVP yes" value={stats.rsvp_yes} accent="text-[#00e599]" />
          <Stat label="Checked in" value={stats.checked_in} accent="text-[#00e599]" />
          <Stat label="No-show" value={stats.no_show} accent="text-[#ffb800]" />
          <Stat label="Attendance" value={`${stats.attendance_rate}%`} accent="text-[#00e599]" />
        </div>

        <div className="card p-5 mt-4 flex flex-wrap items-center gap-6" data-testid="event-settings-card">
          <div>
            <p className="label mb-1">Invitation mode</p>
            <p className="text-xs text-slate-400 max-w-md">
              Hold passes while you invite people, and require an RSVP before a QR code is issued.
            </p>
          </div>
          <label className="flex items-center gap-2 text-xs text-slate-300">
            <input
              type="checkbox"
              className="accent-[#00e599] w-4 h-4"
              data-testid="passes-released-toggle"
              checked={stats.passes_released}
              onChange={(e) => saveSettings({ passes_released: e.target.checked })}
            />
            Passes released
          </label>
          <label className="flex items-center gap-2 text-xs text-slate-300">
            <input
              type="checkbox"
              className="accent-[#00e599] w-4 h-4"
              data-testid="rsvp-required-toggle"
              checked={stats.rsvp_required}
              onChange={(e) => saveSettings({ rsvp_required: e.target.checked })}
            />
            Require RSVP before issuing a pass
          </label>
          <label className="flex items-center gap-2 text-xs text-slate-300">
            <input
              type="checkbox"
              className="accent-[#00e599] w-4 h-4"
              data-testid="auto-email-toggle"
              checked={stats.auto_email_on_register}
              onChange={(e) => saveSettings({ auto_email_on_register: e.target.checked })}
            />
            Email guests automatically on registration
          </label>
          <label className="flex items-center gap-2 text-xs text-slate-300">
            <input
              type="checkbox"
              className="accent-[#ffb800] w-4 h-4"
              data-testid="forward-enabled-toggle"
              checked={!!stats.forward_enabled}
              disabled={!stats.forward_email}
              onChange={(e) => saveSettings({ forward_enabled: e.target.checked })}
            />
            {stats.forward_email
              ? `Send guest emails to ${stats.forward_email} to forward on`
              : "Forwarding address — add one in Edit details first"}
          </label>
          <label className="flex items-center gap-2 text-xs text-slate-300">
            <input
              type="checkbox"
              className="accent-[#00e599] w-4 h-4"
              data-testid="auto-pass-rsvp-toggle"
              checked={stats.auto_pass_on_rsvp}
              onChange={(e) => saveSettings({ auto_pass_on_rsvp: e.target.checked })}
            />
            Email the pass when they reply yes
          </label>
          <p className="font-mono2 text-[11px] text-slate-400 ml-auto" data-testid="rsvp-summary">
            RSVP · <span className="text-[#00e599]">{stats.rsvp_yes} yes</span> · {stats.rsvp_no} no ·{" "}
            {stats.rsvp_pending} awaiting reply
          </p>
        </div>

        <div className="flex gap-1 mt-10 border-b border-[#252f3e]">
          {tabs.map(([id, label]) => (
            <button
              key={id}
              onClick={() => {
                setTab(id);
                if (id === "delivery") loadDelivery();
                if (id === "report") loadDietary();
              }}
              data-testid={`tab-${id}`}
              className={`px-4 py-2.5 text-sm font-semibold transition-colors border-b-2 -mb-px ${
                tab === id ? "border-[#00e599] text-white" : "border-transparent text-slate-400 hover:text-slate-200"
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {tab === "live" && (
          <div className="grid lg:grid-cols-[1.4fr_1fr] gap-6 mt-6" data-testid="live-tab">
            <div className="card p-5">
              <div className="flex items-center justify-between">
                <p className="label mb-0">Recent check-ins</p>
                <button onClick={loadStats} className="btn-ghost text-xs" data-testid="refresh-live-btn">
                  <RefreshCw size={13} /> Refresh
                </button>
              </div>
              <div className="mt-4 divide-y divide-[#252f3e]">
                {stats.recent_checkins.length === 0 && <p className="text-sm text-slate-500 py-4">No check-ins yet.</p>}
                {stats.recent_checkins.map((c) => (
                  <div key={c.checkin_id} className="flex items-center justify-between py-3" data-testid={`recent-checkin-${c.checkin_id}`}>
                    <span className="text-sm text-slate-100">{c.attendee_name}</span>
                    <span className="font-mono2 text-[11px] text-slate-500">
                      {c.station_id} · {new Date(c.checked_in_at).toLocaleTimeString()}
                    </span>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <StationBoard eventId={eventId} />
              <button onClick={purge} className="mt-5 inline-flex items-center gap-2 text-xs text-red-400 hover:text-red-300" data-testid="purge-data-btn">
                <ShieldOff size={13} /> Anonymise personal data (PDPA)
              </button>
            </div>

            <div className="card p-5 lg:col-span-2" data-testid="award-checklist">
              <p className="label mb-0">Key guests</p>
              {awardRecipients.length === 0 ? (
                <p className="text-sm text-slate-500 mt-3">
                  No one tagged yet — set a Role against a guest on the Attendees tab.
                </p>
              ) : (
                <div className="space-y-5 mt-3">
                  {Object.entries(ROLES).map(([key, role]) => {
                    const rows = awardRecipients.filter((r) => (roleOf(r) || {}).label === role.label);
                    if (rows.length === 0) return null;
                    const arrived = rows.filter((r) => r.checked_in);
                    return (
                      <div key={key} data-testid={`role-group-${key}`}>
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <p className={`font-mono2 text-[11px] uppercase tracking-wider ${role.heading}`}>
                            {role.label}
                          </p>
                          <span
                            className="font-mono2 text-[11px] text-slate-400"
                            data-testid={`role-count-${key}`}
                          >
                            {arrived.length} of {rows.length} arrived
                          </span>
                        </div>
                        <div className="grid sm:grid-cols-2 gap-x-8">
                          {[
                            ["Arrived", arrived],
                            ["Still outstanding", rows.filter((r) => !r.checked_in)],
                          ].map(([heading, list]) => (
                            <div key={heading}>
                              <p className="font-mono2 text-[10px] uppercase tracking-wider text-slate-500 mt-2">
                                {heading} · {list.length}
                              </p>
                              <div className="divide-y divide-[#252f3e]">
                                {list.length === 0 && <p className="text-sm text-slate-600 py-2">—</p>}
                                {list.map((r) => (
                                  <div
                                    key={r.registration_id}
                                    className="flex items-center justify-between gap-3 py-2"
                                    data-testid={`award-row-${r.registration_code}`}
                                  >
                                    <span className="text-sm text-slate-100 truncate">
                                      {[r.salutation, r.first_name, r.last_name].filter(Boolean).join(" ")}
                                    </span>
                                    <span className="font-mono2 text-[11px] text-slate-500 shrink-0">
                                      {r.table ? `Table ${r.table}` : "no table"}
                                      {r.checked_in
                                        ? ` · ${new Date(r.checked_in_at).toLocaleTimeString([], {
                                            hour: "numeric",
                                            minute: "2-digit",
                                          })}`
                                        : ""}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="card p-5 lg:col-span-2" data-testid="whatsapp-panel">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <p className="label mb-0">WhatsApp delivery</p>
                <span
                  className={`font-mono2 text-[11px] px-2.5 py-1 rounded-full border ${
                    wa?.enabled ? "text-[#00e599] border-[#00e599]/30" : "text-[#ffb800] border-[#ffb800]/30"
                  }`}
                  data-testid="whatsapp-status-pill"
                >
                  {wa?.enabled ? `active · ${wa.sender}` : "inactive"}
                </span>
              </div>
              {wa?.enabled ? (
                <div className="mt-4">
                  <div className="flex flex-wrap gap-3">
                    <input
                      className="input max-w-xs"
                      placeholder="+6591234567"
                      data-testid="whatsapp-test-input"
                      value={waPhone}
                      onChange={(e) => setWaPhone(e.target.value)}
                    />
                    <button onClick={testWhatsApp} className="btn-secondary" data-testid="whatsapp-test-btn">
                      <MessageSquare size={15} /> Send test message
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-2 mt-4" data-testid="whatsapp-templates">
                    {Object.entries(wa.templates || {}).map(([kind, sid]) => (
                      <span
                        key={kind}
                        className={`font-mono2 text-[11px] px-2.5 py-1 rounded-full border ${
                          sid ? "text-[#00e599] border-[#00e599]/30" : "text-slate-400 border-[#38475c]"
                        }`}
                      >
                        {kind}: {sid ? `${sid.slice(0, 8)}…` : "plain text"}
                      </span>
                    ))}
                  </div>
                  <p className="text-[11px] text-slate-500 mt-3">
                    Approved templates are used automatically when a Content SID is set (required outside the
                    24-hour reply window). Variables: {wa.template_variables}
                  </p>
                </div>
              ) : (
                <p className="text-xs text-slate-400 mt-3 leading-relaxed">
                  Passes, reminders and surveys go out by email only. To turn WhatsApp on, add{" "}
                  <span className="font-mono2 text-slate-200">
                    {(wa?.missing || ["TWILIO_ACCOUNT_SID", "TWILIO_AUTH_TOKEN", "TWILIO_WHATSAPP_FROM"]).join(", ")}
                  </span>{" "}
                  to the backend environment — no code change needed. Attendees with a phone number are then
                  messaged automatically alongside every email.
                </p>
              )}
            </div>
          </div>
        )}

        {tab === "attendees" && (
          <div className="mt-6" data-testid="attendees-tab">
            <div className="flex gap-3">
              <div className="relative flex-1 max-w-md">
                <Search size={15} className="absolute left-3 top-3 text-slate-500 z-10" />
                <input
                  className="input !pl-9"
                  placeholder="Search name, email or reference"
                  data-testid="attendee-search-input"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                />
              </div>
            </div>
            <div className="flex flex-wrap gap-2 mt-4" data-testid="attendee-filters">
              {[
                ["all", "Everyone"],
                ["rsvp_yes", "RSVP yes"],
                ["rsvp_no", "RSVP no"],
                ["rsvp_pending", "No reply yet"],
                ["checked_in", "Checked in"],
                ["waitlisted", "Waitlisted"],
              ].map(([id, label]) => (
                <button
                  key={id}
                  onClick={() => setRegFilter(id)}
                  className={`font-mono2 text-[11px] px-3 py-1.5 rounded-full border transition-colors ${
                    regFilter === id
                      ? "border-[#00e599] text-[#00e599] bg-[#00e599]/10"
                      : "border-[#38475c] text-slate-400 hover:text-slate-200"
                  }`}
                  data-testid={`attendee-filter-${id}`}
                >
                  {label}
                </button>
              ))}
              <span className="font-mono2 text-[11px] text-slate-500 self-center ml-1"
                data-testid="attendee-filter-count">
                {regs.length} shown
              </span>
              <div className="ml-auto flex flex-wrap items-center gap-2">
                <select
                  className="input py-1.5 px-2 text-xs w-[150px]"
                  data-testid="attendee-table-filter"
                  value={tableFilter}
                  onChange={(e) => setTableFilter(e.target.value)}
                >
                  <option value="">All tables</option>
                  {tables.tables.map((t) => (
                    <option key={t.table} value={t.table}>
                      {t.table} ({t.count})
                    </option>
                  ))}
                  {tables.unassigned > 0 && (
                    <option value="__none">No table yet ({tables.unassigned})</option>
                  )}
                </select>
                <button
                  onClick={() => {
                    const next = !groupTables;
                    setGroupTables(next);
                    if (next) setRegSort("table_asc");
                  }}
                  className={`font-mono2 text-[11px] whitespace-nowrap px-3 py-1.5 rounded-full border transition-colors ${
                    groupTables
                      ? "border-[#00e599] text-[#00e599] bg-[#00e599]/10"
                      : "border-[#38475c] text-slate-400 hover:text-slate-200"
                  }`}
                  data-testid="attendee-group-tables-btn"
                  title="Group the list under a heading for each table"
                >
                  Group by table
                </button>
                <div className="w-[180px]">
                <select
                  className="input py-1.5 px-2 text-xs"
                  data-testid="attendee-sort"
                  value={regSort}
                  onChange={(e) => setRegSort(e.target.value)}
                >
                {[
                  ["rsvp_desc", "Latest RSVP first"],
                  ["rsvp_asc", "Oldest RSVP first"],
                  ["registered_desc", "Latest registered"],
                  ["name_asc", "Name A–Z"],
                  ["table_asc", "Table A–Z"],
                ].map(([id, label]) => (
                  <option key={id} value={id}>
                    {label}
                  </option>
                ))}
                </select>
                </div>
              </div>
            </div>
            <div className="card mt-5 overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left font-mono2 text-[11px] uppercase tracking-widest text-slate-500 border-b border-[#252f3e]">
                    <th className="px-4 py-3">Salutation</th>
                    <th className="px-4 py-3">Attendee</th>
                    <th className="px-4 py-3 hidden sm:table-cell">Email</th>
                    <th className="px-4 py-3">Ref</th>
                    <th className="px-4 py-3">Role</th>
                    <th className="px-4 py-3">Table</th>
                    <th className="px-4 py-3">RSVP</th>
                    <th className="px-4 py-3 hidden xl:table-cell">Dietary</th>
                    <th className="px-4 py-3 hidden lg:table-cell">Pass</th>
                    <th className="px-4 py-3">Opened</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3">Send</th>
                    <th className="px-4 py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#252f3e]">
                  {attendeeRows.map((r) =>
                    r.__group ? (
                      <tr key={`group-${r.label}`} className="bg-[#111823]">
                        <td
                          colSpan={13}
                          className="px-4 py-2 font-mono2 text-[11px] uppercase tracking-widest text-[#00e599]"
                          data-testid={`table-group-${r.label}`}
                        >
                          {r.label} · {r.count} {r.count === 1 ? "guest" : "guests"}
                        </td>
                      </tr>
                    ) : (
                    <tr key={r.registration_id} data-testid={`attendee-row-${r.registration_code}`}>
                      <td className="px-4 py-3">
                        <select
                          className="input py-1 px-2 text-xs w-[92px]"
                          data-testid={`salutation-select-${r.registration_code}`}
                          value={
                            !r.salutation || SALUTATIONS.includes(r.salutation) ? r.salutation || "" : "__custom"
                          }
                          onChange={(e) => setSalutation(r, e.target.value)}
                        >
                          <option value="">—</option>
                          {SALUTATIONS.map((s) => (
                            <option key={s} value={s}>
                              {s}
                            </option>
                          ))}
                          {r.salutation && !SALUTATIONS.includes(r.salutation) && (
                            <option value="__custom">{r.salutation}</option>
                          )}
                          <option value="__other">Other…</option>
                        </select>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        {r.first_name} {r.last_name}
                        {r.walk_in && (
                          <span
                            className="ml-2 font-mono2 text-[10px] px-2 py-0.5 rounded-full border border-[#ffb800]/40 text-[#ffb800]"
                            data-testid={`walkin-tag-${r.registration_code}`}
                          >
                            walk-in
                          </span>
                        )}
                        {r.guest_role && ROLES[r.guest_role] && (
                          <span
                            className={`ml-2 font-mono2 text-[10px] px-2 py-0.5 rounded-full border ${ROLES[r.guest_role].tag}`}
                            data-testid={`role-tag-${r.registration_code}`}
                          >
                            {ROLES[r.guest_role].short}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 hidden sm:table-cell text-slate-400">{r.email}</td>
                      <td className="px-4 py-3 font-mono2 text-xs text-slate-400">{r.registration_code}</td>
                      <td className="px-4 py-3">
                        <div className="w-[120px]">
                          <select
                            className="input py-1 px-2 text-xs"
                            data-testid={`role-select-${r.registration_code}`}
                            value={r.guest_role || ""}
                            onChange={(e) => saveRole(r, e.target.value)}
                          >
                            <option value="">—</option>
                            <option value="award">Award recipient</option>
                            <option value="vip">VIP</option>
                            <option value="table_ic">Table-IC</option>
                          </select>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="w-[100px]">
                          <input
                            className="input py-1 px-2 text-xs"
                            maxLength={40}
                            placeholder="—"
                            data-testid={`table-input-${r.registration_code}`}
                            defaultValue={r.table || ""}
                            onBlur={(e) => saveTable(r, e.target.value)}
                            onKeyDown={(e) => e.key === "Enter" && e.target.blur()}
                          />
                        </div>
                      </td>
                      <td className="px-4 py-3 font-mono2 text-xs" data-testid={`rsvp-cell-${r.registration_code}`}>
                        <span className={r.rsvp === "yes" ? "text-[#00e599]" : r.rsvp === "no" ? "text-red-400" : "text-slate-500"}>
                          {r.rsvp || "—"}
                        </span>
                        {r.rsvp_at && (
                          <span className="block font-mono2 text-[10px] text-slate-500 mt-0.5 whitespace-nowrap"
                            data-testid={`rsvp-at-${r.registration_code}`}>
                            {new Date(r.rsvp_at).toLocaleString([], {
                              day: "numeric", month: "short", hour: "numeric", minute: "2-digit",
                            })}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 hidden xl:table-cell text-xs text-slate-400 max-w-[180px] truncate"
                        title={r.dietary || ""} data-testid={`dietary-cell-${r.registration_code}`}>
                        {r.dietary || "—"}
                      </td>

                      <td className="px-4 py-3 hidden lg:table-cell">
                        <span
                          className={`font-mono2 text-[11px] px-2 py-1 rounded-full border ${
                            r.checked_in
                              ? "text-[#00e599] border-[#00e599]/30"
                              : r.status === "waitlisted"
                              ? "text-[#ffb800] border-[#ffb800]/30"
                              : "text-slate-400 border-[#38475c]"
                          }`}
                        >
                          {r.checked_in ? "checked in" : r.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 font-mono2 text-[11px]" data-testid={`opened-cell-${r.registration_code}`}>
                        {r.link_opened_at ? (
                          <span className="text-[#00e599]" title={new Date(r.link_opened_at).toLocaleString()}>
                            opened
                          </span>
                        ) : (
                          <span className="text-slate-500">not yet</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <select
                          className="input py-1 px-2 text-xs w-[124px]"
                          data-testid={`stage-select-${r.registration_code}`}
                          value={r.stage}
                          onChange={(e) => setStage(r, e.target.value)}
                        >
                          <option value="pending">Pending</option>
                          <option value="confirmed">Confirmed</option>
                          <option value="checked_in">Checked in</option>
                          <option value="done">Done</option>
                          <option value="declined">Declined</option>
                          <option value="auto">↺ Automatic</option>
                        </select>
                        {r.stage_override && (
                          <p className="font-mono2 text-[10px] text-[#ffb800] mt-1">set manually</p>
                        )}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="flex gap-1.5">
                          {[["invite", "RSVP"], ["notice", "Confirm"], ["ticket", "Pass"], ["reminder", "Remind"], ["survey", "Survey"],
                            ...customs.map((c) => [c.key, c.name.length > 9 ? `${c.name.slice(0, 8)}…` : c.name])].map(
                            ([kind, label]) => (
                              <button
                                key={kind}
                                onClick={() => sendOne(r, kind)}
                                disabled={sending === `${r.registration_id}:${kind}`}
                                className="font-mono2 text-[10px] px-2 py-1 rounded-md border border-[#38475c] text-slate-300 hover:text-[#00e599] hover:border-[#00e599]/50 transition-colors disabled:opacity-40"
                                data-testid={`send-${kind}-${r.registration_code}`}
                                title={`Email the ${kind} message to this attendee only`}
                              >
                                {sending === `${r.registration_id}:${kind}` ? "…" : label}
                              </button>
                            )
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => setNoteAttendee(r)}
                          className={`btn-ghost text-xs ${r.note ? "text-[#ffb800]" : ""}`}
                          title={r.note || "Add an organiser-only note"}
                          data-testid={`note-attendee-${r.registration_code}`}
                        >
                          <StickyNote size={13} fill={r.note ? "currentColor" : "none"} />
                        </button>
                        <button
                          onClick={() => setEditAttendee(r)}
                          className="btn-ghost text-xs"
                          title="Correct this attendee's details"
                          data-testid={`edit-attendee-${r.registration_code}`}
                        >
                          <Pencil size={13} /> Edit
                        </button>
                        <button
                          onClick={() => setDeleteAttendee(r)}
                          className="btn-ghost text-xs text-red-400/80 hover:text-red-400"
                          title="Delete this attendee from the event"
                          data-testid={`delete-attendee-${r.registration_code}`}
                        >
                          <Trash2 size={13} /> Delete
                        </button>
                        {r.status === "waitlisted" ? (
                          <button onClick={() => promote(r)} className="btn-ghost text-xs" data-testid={`promote-${r.registration_code}`}>
                            <ArrowUp size={13} /> Promote
                          </button>
                        ) : !r.checked_in ? (
                          <button onClick={() => manualCheckIn(r)} className="btn-ghost text-xs" data-testid={`manual-checkin-${r.registration_code}`}>
                            Check in
                          </button>
                        ) : (
                          <>
                            <button
                              onClick={() => setBadge({ registration: r, event: stats.event })}
                              className="btn-ghost text-xs"
                              data-testid={`badge-${r.registration_code}`}
                            >
                              <Printer size={13} /> Badge
                            </button>
                            <button
                              onClick={() => undoCheckIn(r)}
                              className="btn-ghost text-xs text-slate-400 hover:text-[#ff6b6b]"
                              data-testid={`undo-checkin-${r.registration_code}`}
                            >
                              <Undo2 size={13} /> Undo check-in
                            </button>
                          </>
                        )}
                        </div>
                      </td>
                    </tr>
                    )
                  )}
                </tbody>
              </table>
              {regs.length === 0 && <p className="text-sm text-slate-500 p-5">No attendees found.</p>}
            </div>
          </div>
        )}

        {tab === "messages" && (
          <MessagesTab eventId={eventId} event={stats.event} onCustomChange={setCustoms} />
        )}

        {tab === "delivery" && (
          <div className="mt-6" data-testid="delivery-tab">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <p className="text-xs text-slate-400">
                Per-attendee delivery of the invitation, confirmation, pass, reminder and survey
                across email and WhatsApp.
              </p>
              <button onClick={loadDelivery} className="btn-ghost text-xs" data-testid="refresh-delivery-btn">
                <RefreshCw size={13} /> Refresh
              </button>
            </div>
            {!delivery ? (
              <p className="text-sm text-slate-500 mt-5">Loading delivery log…</p>
            ) : (
              <>
                {delivery.forward_to && (
                  <p
                    className="text-xs text-[#ffb800] mt-3 border border-[#ffb800]/30 bg-[#ffb800]/5 rounded-lg px-3 py-2"
                    data-testid="forward-notice"
                  >
                    Guest emails are being routed to {delivery.forward_to} to forward on — attendees
                    are not emailed directly.
                  </p>
                )}
                <div className="flex flex-wrap gap-2 mt-4">
                  {Object.entries(delivery.totals).length === 0 && (
                    <p className="text-sm text-slate-500" data-testid="delivery-empty">
                      Nothing sent yet for this event.
                    </p>
                  )}
                  {Object.entries(delivery.totals).map(([key, t]) => (
                    <span key={key} className="font-mono2 text-[11px] px-2.5 py-1 rounded-full border border-[#38475c] text-slate-300">
                      {key} · <span className="text-[#00e599]">{t.sent || 0} sent</span>
                      {t.failed ? <span className="text-red-400"> · {t.failed} failed</span> : null}
                      {t.skipped_not_configured ? (
                        <span className="text-[#ffb800]"> · {t.skipped_not_configured} skipped</span>
                      ) : null}
                    </span>
                  ))}
                </div>
                <div className="card mt-5 overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left font-mono2 text-[11px] uppercase tracking-widest text-slate-500 border-b border-[#252f3e]">
                        <th className="px-4 py-3">Attendee</th>
                        <th className="px-4 py-3">Invite</th>
                        <th className="px-4 py-3">Confirmation</th>
                        <th className="px-4 py-3">Pass</th>
                        <th className="px-4 py-3">Reminder</th>
                        <th className="px-4 py-3">Survey</th>
                        {customs.map((c) => (
                          <th key={c.key} className="px-4 py-3">{c.name}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#252f3e]">
                      {delivery.rows.map((row) => (
                        <tr key={row.registration_id} data-testid={`delivery-row-${row.registration_code}`}>
                          <td className="px-4 py-3">
                            <p className="whitespace-nowrap">{row.name}</p>
                            <p className="font-mono2 text-[11px] text-slate-500">
                              {row.email}
                              {row.phone ? ` · ${row.phone}` : " · no phone"}
                            </p>
                          </td>
                          {["invite", "notice", "ticket", "reminder", "survey", ...customs.map((c) => c.key)].map((kind) => (
                            <td key={kind} className="px-4 py-3">
                              <div className="flex flex-col gap-1">
                                {["email", "whatsapp"].map((channel) => {
                                  const cell = row.channels[`${kind}:${channel}`];
                                  const color =
                                    !cell
                                      ? "text-slate-600 border-[#252f3e]"
                                      : cell.status === "sent"
                                      ? "text-[#00e599] border-[#00e599]/30"
                                      : cell.status === "failed"
                                      ? "text-red-400 border-red-400/30"
                                      : "text-[#ffb800] border-[#ffb800]/30";
                                  return (
                                    <span
                                      key={channel}
                                      className={`font-mono2 text-[10px] px-2 py-0.5 rounded-full border w-fit ${color}`}
                                      title={cell ? `${cell.status} · ${new Date(cell.sent_at).toLocaleString()}${cell.forwarded_to ? ` · routed to ${cell.forwarded_to}` : ""}` : "not sent"}
                                    >
                                      {channel === "email" ? "✉" : "WA"} {cell ? cell.status.replace("skipped_not_configured", "skipped") : "—"}
                                    </span>
                                  );
                                })}
                              </div>
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {delivery.rows.length === 0 && <p className="text-sm text-slate-500 p-5">No attendees yet.</p>}
                </div>
              </>
            )}
          </div>
        )}

        {tab === "report" && (
          <div className="mt-6 grid lg:grid-cols-2 gap-6" data-testid="report-tab">
            <div className="card p-6 lg:col-span-2" data-testid="dietary-card">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <p className="label mb-0">Dietary requirements</p>
                <div className="flex items-center gap-3">
                  <span className="font-mono2 text-[11px] text-slate-400" data-testid="dietary-count">
                    {dietary
                      ? `${dietary.with_needs} of ${dietary.attending} attending`
                      : "loading…"}
                  </span>
                  <button onClick={loadDietary} className="btn-ghost text-xs" data-testid="refresh-dietary-btn">
                    <RefreshCw size={13} /> Refresh
                  </button>
                  <button onClick={downloadDietaryPdf} className="btn-ghost text-xs" data-testid="download-dietary-pdf-btn">
                    <Download size={13} /> PDF for caterer
                  </button>
                  <button onClick={downloadSeatingPdf} className="btn-ghost text-xs" data-testid="download-seating-pdf-btn">
                    <Download size={13} /> Seating sheet
                  </button>
                </div>
              </div>
              {dietary && dietary.rows.length === 0 ? (
                <p className="text-sm text-slate-500 mt-3">
                  No dietary requirements recorded for guests who replied yes.
                </p>
              ) : (
                <div className="mt-3 divide-y divide-[#252f3e]">
                  {(dietary?.rows || []).map((row) => (
                    <div key={row.requirement} className="py-3" data-testid="dietary-row">
                      <div className="flex items-baseline justify-between gap-3">
                        <p className="text-sm text-slate-100">{row.requirement}</p>
                        <span className="font-mono2 text-sm font-bold text-[#00e599] shrink-0">
                          {row.count}
                        </span>
                      </div>
                      <p className="font-mono2 text-[11px] text-slate-500 mt-1">
                        {row.guests
                          .map((g) => (g.table ? `${g.name} (${g.table})` : g.name))
                          .join(" · ")}
                      </p>
                    </div>
                  ))}
                  {dietary && (
                    <p className="font-mono2 text-[11px] text-slate-500 pt-3">
                      {dietary.no_needs} attending guests with no requirement listed
                    </p>
                  )}
                </div>
              )}
            </div>
            <div className="card p-6 lg:col-span-2" data-testid="daily-report-card">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <p className="label mb-0">Daily RSVP report</p>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => downloadReport("csv")}
                    className="btn-ghost text-xs"
                    data-testid="download-report-csv-btn"
                  >
                    <Download size={13} /> CSV
                  </button>
                  <button
                    onClick={() => downloadReport("pdf")}
                    className="btn-ghost text-xs"
                    data-testid="download-report-pdf-btn"
                  >
                    <Download size={13} /> PDF
                  </button>
                  <button
                    onClick={sendDailyReportNow}
                    disabled={sending === "daily-report"}
                    className="btn-secondary text-xs"
                    data-testid="send-daily-report-btn"
                  >
                    <MailPlus size={14} />
                    {sending === "daily-report" ? "Sending…" : "Send to me now"}
                  </button>
                </div>
              </div>
              <p className="text-xs text-slate-400 mt-2 max-w-2xl leading-relaxed">
                A snapshot you can forward to your client: how many replied yes (and how that moved
                since yesterday), who is still to reply, plus a dietary list. The full "replied yes"
                list comes attached as a CSV. Sent at 18:00 Singapore time and stops by itself the
                day after the event.
              </p>
              <label className="flex items-center gap-2 text-xs text-slate-300 mt-4">
                <input
                  type="checkbox"
                  className="accent-[#00e599] w-4 h-4"
                  data-testid="daily-report-toggle"
                  checked={!!stats.daily_report?.enabled}
                  onChange={(e) => setDailyReport(e.target.checked)}
                />
                Email me this report every day at 18:00 SGT
              </label>
              {stats.daily_report?.last_sent_at && (
                <p className="font-mono2 text-[11px] text-slate-500 mt-3" data-testid="daily-report-last-sent">
                  Last sent {new Date(stats.daily_report.last_sent_at).toLocaleString()} ·{" "}
                  {stats.daily_report.last_yes} replied yes at that point
                </p>
              )}
            </div>
            <div className="card p-6">
              <div className="flex items-center justify-between">
                <p className="label mb-0">Attendance report</p>
                <button onClick={() => download("attendance")} className="btn-ghost text-xs" data-testid="export-attendance-btn">
                  <Download size={13} /> CSV
                </button>
                <button onClick={downloadAttendancePdf} className="btn-ghost text-xs" data-testid="export-attendance-pdf-btn">
                  <Download size={13} /> PDF
                </button>
                <button onClick={downloadRsvpExcel} className="btn-ghost text-xs" data-testid="export-rsvp-excel-btn">
                  <Download size={13} /> Excel
                </button>
              </div>
              <div className="space-y-2 font-mono2 text-sm mt-3">
                {[
                  ["Capacity", stats.capacity],
                  ["Confirmed registrations", stats.confirmed],
                  ["Waitlisted", stats.waitlisted],
                  ["Checked in", stats.checked_in],
                  ["No-shows", stats.no_show],
                  ["Attendance rate", `${stats.attendance_rate}%`],
                  ["Survey responses", stats.survey_responses],
                ].map(([k, v]) => (
                  <div key={k} className="flex justify-between border-b border-[#252f3e] pb-2">
                    <span className="text-slate-400">{k}</span>
                    <span className="text-slate-100">{v}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="card p-6">
              <div className="flex items-center justify-between">
                <p className="label mb-0">Survey results</p>
                <button onClick={() => download("survey")} className="btn-ghost text-xs" data-testid="export-survey-btn">
                  <Download size={13} /> Export CSV
                </button>
              </div>
              {!survey || survey.count === 0 ? (
                <p className="text-sm text-slate-500 mt-3" data-testid="no-survey-results">
                  No feedback yet — send the survey after the event.
                </p>
              ) : (
                <>
                  <p className="text-xs text-slate-400 mt-3">{survey.count} response(s)</p>
                  <div className="mt-4 space-y-5 max-h-[520px] overflow-y-auto pr-1">
                    {(survey.questions || []).map((q) => (
                      <div key={q.id} data-testid={`survey-result-${q.id}`}>
                        <p className="text-sm text-slate-200">{q.label}</p>
                        {q.type === "stars" && (
                          <>
                            <p className="font-mono2 text-xs text-[#00e599] mt-1">
                              {q.average} average · {q.answered} answered
                            </p>
                            <div className="mt-2 space-y-1.5">
                              {q.distribution
                                .slice()
                                .reverse()
                                .map((d) => (
                                  <div key={d.stars} className="flex items-center gap-3 font-mono2 text-[11px]">
                                    <span className="w-6 text-slate-500">{d.stars}★</span>
                                    <div className="flex-1 h-2 bg-[#1c2430] rounded-full overflow-hidden">
                                      <div
                                        className="h-full bg-[#00e599]"
                                        style={{ width: `${(d.count / Math.max(q.answered, 1)) * 100}%` }}
                                      />
                                    </div>
                                    <span className="text-slate-400">{d.count}</span>
                                  </div>
                                ))}
                            </div>
                          </>
                        )}
                        {q.type === "yesno" && (
                          <p className="font-mono2 text-xs text-slate-400 mt-1">
                            <span className="text-[#00e599]">{q.yes_rate}% yes</span> · {q.yes} yes / {q.no} no
                          </p>
                        )}
                        {q.type === "text" && (
                          <div className="mt-2 space-y-2">
                            {q.answers.length === 0 && <p className="text-xs text-slate-500">No answers yet.</p>}
                            {q.answers.map((a, i) => (
                              <p key={i} className="text-xs text-slate-300 border-l-2 border-[#00e599]/40 pl-3">
                                "{a.text}"
                              </p>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {editing && (
          <EventForm
            event={stats.event}
            onClose={() => setEditing(false)}
            onSaved={(updated) => setStats({ ...stats, event: updated })}
          />
        )}

        {confirm && createPortal(
          <div className="fixed inset-0 z-[100] bg-black/80 grid place-items-center p-4"
            onClick={() => setConfirm(null)} data-testid="bulk-confirm-modal">
            <div onClick={(e) => e.stopPropagation()} className="card p-7 max-w-lg w-full">
              <div className="flex items-start gap-3">
                <AlertTriangle size={20} className="text-[#ffb800] shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-display text-lg font-black">
                    Send {labelOf(confirm.kind, customs).toLowerCase()} to {confirm.count} attendee
                    {confirm.count === 1 ? "" : "s"}?
                  </h3>
                  <p className="text-sm text-slate-400 mt-2">
                    This emails <strong data-testid="bulk-confirm-count">{confirm.count}</strong>{" "}
                    attendee{confirm.count === 1 ? "" : "s"}{" "}
                    {confirm.onlyFailed ? "whose last send failed" : confirm.describes}. It cannot
                    be undone.
                  </p>
                </div>
              </div>
              {confirm.sample?.length > 0 && (
                <p className="font-mono2 text-[11px] text-slate-500 mt-4 break-all">
                  {confirm.sample.join(", ")}
                  {confirm.count > confirm.sample.length && ` +${confirm.count - confirm.sample.length} more`}
                </p>
              )}
              {confirm.count > 100 && (
                <p className="font-mono2 text-[11px] text-[#ffb800] mt-3">
                  Over 100 emails — your provider's daily cap may pause the run partway. Anything
                  not sent stays retryable.
                </p>
              )}
              <div className="flex gap-3 mt-6">
                <button onClick={runSend} className="btn-primary" data-testid="bulk-confirm-send-btn">
                  Yes, send now
                </button>
                <button
                  onClick={() => {
                    setScheduling({
                      kind: confirm.kind,
                      onlyFailed: confirm.onlyFailed,
                      audience: confirm.audience,
                      when: defaultWhen(),
                    });
                    setConfirm(null);
                  }}
                  className="btn-secondary"
                  data-testid="bulk-confirm-schedule-btn"
                >
                  <Clock size={15} /> Schedule instead
                </button>
                <button onClick={() => setConfirm(null)} className="btn-secondary"
                  data-testid="bulk-confirm-cancel-btn">
                  Cancel
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

        {scheduling && createPortal(
          <div className="fixed inset-0 z-[100] bg-black/80 grid place-items-center p-4"
            onClick={() => setScheduling(null)} data-testid="schedule-modal">
            <div onClick={(e) => e.stopPropagation()} className="card p-7 max-w-lg w-full">
              <div className="flex items-start gap-3">
                <Clock size={20} className="text-[#00e599] shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-display text-lg font-black">
                    Schedule {labelOf(scheduling.kind, customs).toLowerCase()}
                  </h3>
                  <p className="text-sm text-slate-400 mt-2">
                    Pick a date and time in Singapore time (GMT+8). Recipients are worked out when
                    the send fires, so anyone who registers before then is included.
                  </p>
                </div>
              </div>
              <label className="label mt-6 block">Date & time (SGT)</label>
              <input
                type="datetime-local"
                className="input"
                step={900}
                data-testid="schedule-datetime-input"
                value={scheduling.when}
                onChange={(e) => setScheduling({ ...scheduling, when: e.target.value })}
              />
              <p className="font-mono2 text-[11px] text-slate-500 mt-3">
                Sends fire on the next 15-minute check after the time you choose. If the email
                provider's daily cap is hit part-way, the rest stay retryable and I'll show what's
                left in the delivery log.
              </p>
              <div className="flex gap-3 mt-6">
                <button onClick={saveSchedule} className="btn-primary" data-testid="schedule-save-btn">
                  {scheduling.replace ? "Reschedule" : "Schedule send"}
                </button>
                {scheduling.replace && (
                  <button
                    onClick={async () => {
                      await cancelSchedule(scheduling.replace);
                      setScheduling(null);
                    }}
                    className="btn-secondary"
                    data-testid="schedule-modal-cancel-send-btn"
                  >
                    Cancel this send
                  </button>
                )}
                <button onClick={() => setScheduling(null)} className="btn-secondary"
                  data-testid="schedule-close-btn">
                  Close
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

        {deleteAttendee && createPortal(
          <div className="fixed inset-0 z-[100] bg-black/80 grid place-items-center p-4"
            onClick={() => setDeleteAttendee(null)} data-testid="delete-attendee-modal">
            <div onClick={(e) => e.stopPropagation()} className="card p-7 max-w-lg w-full">
              <div className="flex items-start gap-3">
                <AlertTriangle size={20} className="text-red-400 shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-display text-lg font-black">
                    Delete {deleteAttendee.first_name} {deleteAttendee.last_name}?
                  </h3>
                  <p className="font-mono2 text-[11px] text-slate-400 mt-2" data-testid="delete-attendee-details">
                    {deleteAttendee.email} · ref {deleteAttendee.registration_code}
                  </p>
                  <p className="text-sm text-slate-400 mt-3">
                    This permanently removes their registration, QR pass, check-in, survey answer
                    and delivery history, and frees a capacity seat. It cannot be undone. No one
                    else is affected.
                  </p>
                </div>
              </div>
              <div className="flex gap-3 mt-6">
                <button
                  onClick={removeAttendee}
                  className="inline-flex items-center gap-2 bg-red-500 hover:bg-red-400 text-white font-bold text-sm px-5 py-2.5 rounded-lg transition-colors"
                  data-testid="delete-attendee-confirm-btn"
                >
                  <Trash2 size={15} /> Delete attendee
                </button>
                <button onClick={() => setDeleteAttendee(null)} className="btn-secondary"
                  data-testid="delete-attendee-cancel-btn">
                  Cancel
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

        {editAttendee && (
          <AttendeeEdit
            eventId={eventId}
            attendee={editAttendee}
            onClose={() => setEditAttendee(null)}
            onSaved={(updated) =>
              setRegs((prev) =>
                prev.map((x) =>
                  x.registration_id === updated.registration_id ? { ...x, ...updated } : x
                )
              )
            }
          />
        )}

        {noteAttendee && (
          <AttendeeNote
            eventId={eventId}
            attendee={noteAttendee}
            onClose={() => setNoteAttendee(null)}
            onSaved={(note) =>
              setRegs((prev) =>
                prev.map((x) =>
                  x.registration_id === noteAttendee.registration_id ? { ...x, note } : x
                )
              )
            }
          />
        )}


        {badge && (
          <div className="fixed inset-0 z-[60] bg-black/80 grid place-items-center p-4" onClick={() => setBadge(null)}>
            <div onClick={(e) => e.stopPropagation()} className="flex flex-col items-center gap-4">
              <Badge registration={badge.registration} event={badge.event} />
              <div className="flex gap-3">
                <button onClick={() => window.print()} className="btn-primary" data-testid="print-badge-btn">
                  <Printer size={15} /> Print badge
                </button>
                <button onClick={() => setBadge(null)} className="btn-secondary" data-testid="close-badge-btn">
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Shell>
  );
}
